import { DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { RpsPrestadoresOdontologicos } from '@gestor-web/@core/api/ords/model';
import { LayoutActions, OperadorasActions } from '@gestor-web/@core/store/actions';
import { LayoutSelectors, OperadorasSelectors } from '@gestor-web/@core/store/selectors';
import { filterToQueryString } from '@gestor-web/@core/utils';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { NotificationService, PermissaoService } from '@gestor-web/app/shared/services';
import { GridService } from '@gestor-web/app/shared/services/grid.service';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { GridOptions } from 'ag-grid-enterprise';
import { AgFilterUtils, buildGridOptions, exportGridAsExcel } from '@gestor-web/app/shared/components';
import { Observable, Subject, combineLatest } from 'rxjs';
import { filter, first, map, takeUntil } from 'rxjs/operators';
import { RpsPrestadoresOdontologicosService } from './rps-prestadores-odontologicos.service';
import { FUNCIONALIDADE } from '@gestor-web/@core/auth/funcionalidades';

const GRID_KEY = 'ANALISES.RPS_PRESTADORES_ODONTOLOGICOS';

const REGISTRO_RPS_OPTIONS = [
  { label: 'Registrado', value: 1 },
  { label: 'Não registrado', value: 2 },
];

const COLUMNS: ColDef[] = [
  {
    headerName: 'Prestador informado na Guia',
    field: 'ds_prestador',
    width: 380,
  },
  {
    headerName: 'Qtde. rede direta',
    field: 'qtde_rede_direta',
    type: 'int',
    width: 160,
  },
  {
    headerName: 'Qtde. rede indireta',
    field: 'qtde_rede_indireta',
    type: 'int',
    width: 170,
  },
];

@Component({
  templateUrl: './rps-prestadores-odontologicos.component.html',
  providers: [RpsPrestadoresOdontologicosService, DecimalPipe],
  standalone: false,
})
export class RpsPrestadoresOdontologicosComponent implements OnInit, OnDestroy {
  readonly GRID_KEY = GRID_KEY;
  readonly registroRpsOptions = REGISTRO_RPS_OPTIONS;

  @ViewChild('agGrid') agGrid: AgGridAngular<RpsPrestadoresOdontologicos>;

  gridOptions: GridOptions<RpsPrestadoresOdontologicos>;

  pageReady$ = new Subject<void>();
  filterReady$ = new Subject<boolean>();
  firstLoad = true;
  filterChanged = true;

  podeGerenciarVisoes = false;

  operadora: string;
  operadoraNome: string;

  competenciaRange: string[];
  selectedRegistroRps: number = null;

  rowData: RpsPrestadoresOdontologicos[];

  pageTooltip = 'RELATORIOS_PERSONALIZADOS.RPS_PRESTADORES_ODONTOLOGICOS';

  fullscreen$: Observable<boolean>;
  gridHeight$: Observable<string>;

  constructor(
    private permissaoService: PermissaoService,
    private service: RpsPrestadoresOdontologicosService,
    private decimalPipe: DecimalPipe,
    private notificationService: NotificationService,
    private gridService: GridService,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.fullscreen$ = this.store.select(LayoutSelectors.selectFullscreen);
    this.gridHeight$ = this.fullscreen$.pipe(
      map(fullscreen => (fullscreen ? 'calc(100vh - 130px)' : 'calc(100vh - 186px)')),
    );
  }

  ngOnInit(): void {
    const ano = new Date().getFullYear();
    this.competenciaRange = [`${ano}01`, `${ano}12`];

    this.store
      .select(OperadorasSelectors.selectWorkspaceOperadora)
      .pipe(
        filter(op => !!op),
        concatLatestFrom(() => this.store.select(OperadorasSelectors.selectWorkspaceParams)),
        takeUntil(this.ngDestroyed$),
      )
      .subscribe({
        next: ([operadora, params]) => {
          this.operadora = operadora.cd_registro_ans;
          this.operadoraNome = operadora.sg_operadora;
          this.competenciaRange = [
            params?.competencia ? '' + params.competencia : null,
            params?.competencia_final ? '' + params.competencia_final : null,
          ];

          setTimeout(() => this.pageReady$.next());
        },
        error: error => this.notificationService.handleError(error),
      });

    combineLatest([this.route.queryParams, this.pageReady$])
      .pipe(first())
      .subscribe(([params, _]) => this.applyQueryFilters(params));

    combineLatest([this.filterReady$, this.pageReady$])
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(([hasFilter, _]) => {
        if (!this.firstLoad || hasFilter) {
          this.reloadData();
        }
      });

    this.permissaoService
      .checkAcessoFeature(FUNCIONALIDADE.GERENCIAR_VISOES)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(podeGerenciarVisoes => {
        this.podeGerenciarVisoes = podeGerenciarVisoes;
        this.configureGrid();
      });
  }

  private configureGrid() {
    this.gridOptions = buildGridOptions<RpsPrestadoresOdontologicos>(
      {
        columnDefs: COLUMNS,
        pipes: {
          decimal: this.decimalPipe,
        },
        views: {
          gridKey: GRID_KEY,
          hide: !this.podeGerenciarVisoes,
        },
        exportExcelFn: () => this.exportExcel(),
      },
      {
        context: {
          parentComponent: this,
        },
      },
    );
  }

  applyQueryFilters(params: Params) {
    const { competencia, competencia_final, registro_rps, visao, sort, extraFilters } = params;
    let hasFilter = false;

    if (competencia && competencia_final) {
      this.competenciaRange = [competencia, competencia_final];
    }

    if (registro_rps) {
      this.selectedRegistroRps = Number(registro_rps);
    }

    if (sort || extraFilters) {
      hasFilter = true;
    }

    const filterModel = this.gridService.getDecodedFilter(extraFilters);
    setTimeout(() => this.gridService.applyVisaoInicial(GRID_KEY, this.agGrid, visao, sort, filterModel));

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });

    this.filterReady$.next(hasFilter);
  }

  reloadData() {
    if (!this.competenciaRange?.[0] || !this.competenciaRange?.[1]) {
      this.rowData = [];
      return;
    }

    if (this.firstLoad) {
      this.firstLoad = false;
      this.agGrid?.api?.setGridOption('loading', false);
      setTimeout(() => {
        this.agGrid?.api?.setGridOption('loading', true);
      });
    } else {
      this.agGrid?.api?.setGridOption('loading', true);
    }

    this.filterChanged = false;

    this.service
      .getData(
        this.operadora,
        this.competenciaRange[0],
        this.competenciaRange[1],
        this.selectedRegistroRps ?? undefined,
      )
      .subscribe({
        next: items => {
          this.rowData = items ?? [];
        },
        error: error => this.notificationService.handleError(error),
        complete: () => this.agGrid?.api?.setGridOption('loading', false),
      });
  }

  competenciaChanged(range: string[]) {
    this.competenciaRange = range;
    this.filterChanged = true;
    this.store.dispatch(
      OperadorasActions.changeWorkspaceParams({
        partialParams: {
          competencia: +range[0],
          competencia_final: +range[1],
        },
      }),
    );
  }

  onRegistroRpsChange() {
    this.filterChanged = true;
  }

  toggleFullscreen() {
    this.store.dispatch(LayoutActions.toggleFullscreen({}));
  }

  exportExcel() {
    const visao = this.gridService.getCurrentVisao(GRID_KEY);
    const visaoNome = visao ? `- Visão ${visao?.nm_visao}` : '';

    const fileNamePrefix = `rps-prestadores-odontologicos-${this.operadoraNome}-${this.competenciaRange[0]}-${this.competenciaRange[1]}`;

    const registroRpsTexto =
      this.registroRpsOptions.find(opt => opt.value === this.selectedRegistroRps)?.label ?? 'Todos';

    const headers = [
      `RPS x Prestadores odontológicos ${visaoNome}`,
      `${this.operadoraNome} - ${this.operadora}`,
      'Competência: ' + AgFilterUtils.competenciaRangeToString(this.competenciaRange),
      'Situação no RPS: ' + registroRpsTexto,
    ];

    exportGridAsExcel({
      api: this.agGrid.api,
      headers,
      fileNamePrefix,
    });
  }

  onCopyUrlFilters() {
    const visao = this.gridService.getCurrentVisao(GRID_KEY);
    const sort = this.gridService.getSimpleSort(this.agGrid);
    const extraFilters = this.gridService.getEncodedFilter(this.agGrid);

    const urlFilter = {
      competencia: this.competenciaRange[0],
      competencia_final: this.competenciaRange[1],
      registro_rps: this.selectedRegistroRps ?? undefined,
      visao: visao?.cd_visao,
      sort,
      extraFilters,
    };

    const url = filterToQueryString(urlFilter);
    navigator.clipboard.writeText(url);
    this.notificationService.info('Endereço com filtros copiado');
  }

  private ngDestroyed$ = new Subject<void>();
  ngOnDestroy() {
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }
}
